import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { ArrowCircleRightIcon } from '@10xLabs/react-web/components/Icons/ArrowCircleRight'
import { Dropdown } from 'components/v2/Dropdown'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { StyledLink } from 'components/v2/Link'
import { LabelWrapper, Label, ReturnTripsWrapper, ReturnIcon } from '../Service'
import { passengerTypeMessages } from 'features/Bookings/components/PassengerType/messages'
import { isTicketCancelled } from 'resolvers/bookings'
import { safeFormatMessage } from 'utils/intl'
import { sanitizePrice } from 'utils/math'
import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { GTBookingV2, GTBookingV2Trip } from 'gql/types.generated'
import { ITicketModel } from 'features/Bookings/types'

const Table = styled.table<TSFixMe>`
  width: 100%;
  border-collapse: collapse;
`

const Tr = styled.tr<TSFixMe>`
  background-color: #ffffff;

  :hover {
    background-color: #f2f2f2;
  }
`

const Td = styled.td<TSFixMe>`
  padding: 10px;
`

const PassengerNameCell = styled(Td)<TSFixMe>`
  white-space: nowrap;
`
const PropertyLabel = styled.span<TSFixMe>`
  margin-left: 5px;
`

interface ITicketsProps {
  tickets: ITicketModel[]
  booking: GTBookingV2
}

const Tickets = ({ tickets, booking }: ITicketsProps): ReactElement => {
  const intl = useIntl()
  const pricesCurrency = booking.currency.code === 'USD' ? 'USD' : undefined
  return (
    <div>
      {tickets.length > 0 ? (
        <Dropdown toggle={<StyledLink>{tickets.length}</StyledLink>} noPadding fitContent>
          <Table>
            <tbody>
              {tickets.map((ticket, index): ReactElement => {
                const { seat, subtotal, discount, vat, paidAmount } = ticket
                const properties = ticket.passenger?.properties ?? []
                const { firstName, lastName } = ticket.passenger || {}
                const isFullyPaid = paidAmount >= subtotal - discount + vat
                const propertiesLabel = properties
                  .map(({ name }: { name: string }) => name)
                  .filter(Boolean)
                  .join(' -')
                return (
                  <Tr key={index}>
                    <PassengerNameCell>
                      {!firstName && !lastName ? 'N/A' : `${firstName} ${lastName}`}
                    </PassengerNameCell>
                    <Td>{seat?.label}</Td>
                    <Td>
                      {safeFormatMessage({
                        intl,
                        messages: passengerTypeMessages,
                        key: ticket.passenger?.type.name || ''
                      })}
                      {!isEmpty(properties) ? <PropertyLabel>{`(${propertiesLabel})`}</PropertyLabel> : null}
                    </Td>
                    <Td>
                      <FormattedPrice value={sanitizePrice(subtotal - discount + vat)} currency={pricesCurrency} />
                    </Td>
                    <Td>
                      {isFullyPaid ? (
                        <FormattedMessage id="booking.tickets.paid" defaultMessage="Paid" />
                      ) : (
                        <FormattedMessage id="booking.tickets.pending" defaultMessage="Pending" />
                      )}
                    </Td>
                  </Tr>
                )
              })}
            </tbody>
          </Table>
        </Dropdown>
      ) : (
        0
      )}
    </div>
  )
}

interface IBookingTicketsProps {
  booking: GTBookingV2
  tickets: ITicketModel[]
  departureTrips: GTBookingV2Trip[]
  returnTrips: GTBookingV2Trip[]
  showDepartureTrips: boolean
  showReturnTrips: boolean
}

export const BookingTickets = ({
  booking,
  tickets,
  departureTrips,
  returnTrips,
  showDepartureTrips,
  showReturnTrips
}: IBookingTicketsProps): ReactElement => {
  return (
    <div>
      {showDepartureTrips && (
        <>
          {showReturnTrips && (
            <LabelWrapper>
              <ArrowCircleRightIcon />
              <Label>
                <FormattedMessage id="dictionary.departure" defaultMessage="Departure" />
              </Label>
            </LabelWrapper>
          )}

          {departureTrips.map((trip, index) => {
            const tripTickets = tickets.filter(
              (ticket): TSFixMe => ticket.trip?.id === trip.id && !isTicketCancelled(ticket)
            )

            return <Tickets key={index} tickets={tripTickets} booking={booking} />
          })}
        </>
      )}

      {showReturnTrips && (
        <ReturnTripsWrapper showDepartureTrips={showDepartureTrips}>
          {showDepartureTrips && (
            <LabelWrapper>
              <ReturnIcon />

              <Label>
                <FormattedMessage id="dictionary.return" defaultMessage="Return" />
              </Label>
            </LabelWrapper>
          )}

          {returnTrips.map((trip, index): ReactElement => {
            const tripTickets = tickets.filter((ticket) => ticket.trip?.id === trip.id && !isTicketCancelled(ticket))

            return <Tickets key={index} tickets={tripTickets} booking={booking} />
          })}
        </ReturnTripsWrapper>
      )}
    </div>
  )
}
